/**
 * @fileoverview Present worker, queue, task-history, and result observations with explicit operational controls. Backend endpoints own permitted actions and actual worker state. Broker/task observations are distinct from durable business-ledger outcomes. Refreshing a dashboard does not start work or establish that a provider effect completed.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  AppButton,
  AppModal,
  AppNumberInput,
  AppSelect,
  AppSpinner,
  AppTag,
  AppTextarea,
  AppTextInput
} from '../shared';
import api from '../../api';
import ToastContext from '../../ToastContext';
import './CeleryDashboard.css';

const CONFIRMATION_TOKEN = 'CONFIRM';
const EMPTY_LIST = [];

const TASK_STATE_OPTIONS = [
  { label: 'All live tasks', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Reserved', value: 'reserved' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Revoked', value: 'revoked' },
  { label: 'History only', value: 'history' },
];



function shortName(value) {
  return String(value || '').split('.').pop() || 'unknown';
}



function taskStateSeverity(state) {
  if (state === 'active') return 'warning';
  if (state === 'reserved') return 'info';
  if (state === 'scheduled') return 'info';
  if (state === 'revoked') return 'danger';
  if (state === 'success') return 'success';
  if (state === 'error') return 'danger';
  return 'info';
}



function parseJsonField(value, fallback, label) {
  const text = String(value || '').trim();
  if (!text) return fallback;
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(fallback) && !Array.isArray(parsed)) {
      throw new Error(`${label} must be a JSON array`);
    }
    if (!Array.isArray(fallback) && (parsed === null || Array.isArray(parsed) || typeof parsed !== 'object')) {
      throw new Error(`${label} must be a JSON object`);
    }
    return parsed;
  } catch (error) {
    throw new Error(error.message || `${label} is invalid JSON`);
  }
}



function formatJsonPreview(value) {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
}



function CeleryDashboard() {
  const showToast = useContext(ToastContext);
  const [overview, setOverview] = useState(null);
  const [workersPayload, setWorkersPayload] = useState(null);
  const [tasksPayload, setTasksPayload] = useState(null);
  const [queuesPayload, setQueuesPayload] = useState(null);
  const [knownTasksPayload, setKnownTasksPayload] = useState(null);
  const [panelErrors, setPanelErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [taskFilters, setTaskFilters] = useState({ state: 'all', q: '' });
  const [selectedQueueName, setSelectedQueueName] = useState('');
  const [selectedQueueTasks, setSelectedQueueTasks] = useState({});
  const [workerControl, setWorkerControl] = useState({ worker: '', n: 1, min: 1, max: 4, queue: 'celery' });
  const [limitControl, setLimitControl] = useState({ task: '', worker: '', rate_limit: '10/m', soft: 540, hard: 600 });
  const [runControl, setRunControl] = useState({ task: '', args: '[]', kwargs: '{}', options: '{}' });
  const [resultTaskId, setResultTaskId] = useState('');
  const [taskResult, setTaskResult] = useState(null);
  const [actionPending, setActionPending] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');

  const notify = useCallback(

  (payload) => {
    if (typeof showToast === 'function') {
      showToast(payload);
    }
  }, [showToast]);

  const workers = workersPayload?.workers || overview?.workers || EMPTY_LIST;
  const workerOptions = workers.map((worker) => ({ label: worker.name, value: worker.name }));
  const knownTasks = knownTasksPayload?.tasks || EMPTY_LIST;
  const taskOptions = knownTasks.map((task) => ({
    label: task.job_name ? `${task.short_name} (${task.job_name})` : task.task_name,
    value: task.task_name,
  }));
  const queues = queuesPayload?.queues || EMPTY_LIST;
  const queueOptions = queues.map((queue) => ({ label: queue.queue, value: queue.queue }));
  const currentQueue = useMemo(
    () => queues.find((queue) => queue.queue === selectedQueueName) || queues[0] || null,
    [queues, selectedQueueName]
  );
  const queueGroups = useMemo(() => currentQueue?.task_groups || EMPTY_LIST, [currentQueue]);
  const displayTasks = useMemo(

  () => {
    const tasks = tasksPayload?.tasks || [];
    const state = taskFilters.state;
    const query = taskFilters.q.trim().toLowerCase();
    return tasks.filter((task) => {
      if (state !== 'all' && state !== 'history' && task.state !== state) {
        return false;
      }
      if (!query) {
        return true;
      }
      return ['id', 'task_name', 'short_name', 'args', 'kwargs', 'worker']
        .some((key) => String(task[key] || '').toLowerCase().includes(query));
    });
  }, [taskFilters.q, taskFilters.state, tasksPayload]);

  const displayLogs = useMemo(

  () => {
    const logs = tasksPayload?.recent_logs || [];
    const query = taskFilters.q.trim().toLowerCase();
    if (!query) {
      return logs;
    }
    return logs.filter((log) =>
      ['message', 'task_name', 'task_id', 'job_id', 'status', 'action', 'name']
        .some((key) => String(log[key] || '').toLowerCase().includes(query))
    );
  }, [taskFilters.q, tasksPayload]);

  const selectedQueueTaskNames = useMemo(() =>
    Object.entries(selectedQueueTasks)
      .filter(([, selected]) => Boolean(selected))
      .map(([taskName]) => taskName),
  [selectedQueueTasks]);

  const selectedQueueMessageCount = useMemo(() => {
    const selected = new Set(selectedQueueTaskNames);
    return queueGroups.reduce((total, group) =>
      selected.has(group.task_name) ? total + Number(group.count || 0) : total,
    0);
  }, [queueGroups, selectedQueueTaskNames]);

  const fetchDashboard = useCallback(

  async () => {
    setLoading(true);
    try {
      const response = await api.get('/automations/celery/overview');
      const data = response.data || {};
      setOverview(data);
      setWorkersPayload({ success: true, workers: data.workers || [], worker_count: data.worker_count || 0 });
      setTasksPayload({
        success: true,
        tasks: data.tasks || [],
        recent_logs: data.recent_logs || [],
        counts: {
          active: data.active_count || 0,
          reserved: data.reserved_count || 0,
          scheduled: data.scheduled_count || 0,
          revoked: data.revoked_count || 0,
        },
      });
      setQueuesPayload({
        success: (data.queue_errors || []).length === 0,
        queues: data.queues || [],
        total: data.queued_count || 0,
        errors: data.queue_errors || [],
      });
      setKnownTasksPayload({ success: true, tasks: data.known_tasks || [] });

      const workerNames = (data.workers || []).map((worker) => worker.name);
      const taskNames = (data.known_tasks || []).map((task) => task.task_name);
      const queueNames = (data.queues || []).map((queue) => queue.queue);
      setWorkerControl((previous) => ({ ...previous, worker: previous.worker || workerNames[0] || '' }));
      setLimitControl((previous) => ({ ...previous, task: previous.task || taskNames[0] || '' }));
      setRunControl((previous) => ({ ...previous, task: previous.task || taskNames[0] || '' }));
      setSelectedQueueName((previous) => queueNames.includes(previous) ? previous : queueNames[0] || '');
      setPanelErrors((data.queue_errors || []).length > 0 ? { queues: 'One or more broker queues could not be inspected.' } : {});
      setLastRefresh(new Date());
    } catch (error) {
      setPanelErrors({
        dashboard: error.response?.data?.error || error.message || 'Dashboard request failed',
      });
      notify({
        severity: 'error',
        summary: 'Celery dashboard failed',
        detail: error.response?.data?.error || error.message || 'Dashboard request failed',
        life: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    const interval = setInterval(fetchDashboard, 60000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const executeAction = useCallback(

  async (action) => {
    setActionPending(true);
    try {
      const payload = action.highRisk ?
        { ...(action.payload || {}), confirmation: CONFIRMATION_TOKEN } :
        action.payload || {};
      const response = await api.post(action.endpoint, payload);
      notify({
        severity: response.status === 202 ? 'info' : 'success',
        summary: action.successSummary || 'Celery updated',
        detail: response.data?.message || 'Action completed',
        life: 3000,
      });
      setConfirmAction(null);
      setConfirmationText('');
      setSelectedQueueTasks({});
      await fetchDashboard();
    } catch (error) {
      notify({
        severity: 'error',
        summary: 'Celery action failed',
        detail: error.response?.data?.error || error.response?.data?.message || error.message || 'Action failed',
        life: 4000,
      });
    } finally {
      setActionPending(false);
    }
  }, [fetchDashboard, notify]);

  const runAction = useCallback(

  (action) => {
    if (action.highRisk) {
      setConfirmAction(action);
      setConfirmationText('');
      return;
    }
    executeAction(action);
  }, [executeAction]);

  const requireWorker =

  () => {
    if (!workerControl.worker) {
      notify({ severity: 'warn', summary: 'Select worker', detail: 'Choose a Celery worker first.', life: 3000 });
      return false;
    }
    return true;
  };

  const handleTaskRun =

  () => {
    if (!runControl.task) {
      notify({ severity: 'warn', summary: 'Select task', detail: 'Choose a known task first.', life: 3000 });
      return;
    }
    try {
      const args = parseJsonField(runControl.args, [], 'args');
      const kwargs = parseJsonField(runControl.kwargs, {}, 'kwargs');
      const options = parseJsonField(runControl.options, {}, 'options');
      runAction({
        endpoint: `/automations/celery/tasks/${encodeURIComponent(runControl.task)}/run`,
        payload: { args, kwargs, options },
        successSummary: 'Task queued',
      });
    } catch (error) {
      notify({ severity: 'error', summary: 'Invalid JSON', detail: error.message, life: 4000 });
    }
  };

  const handleResultLookup =

  async () => {
    if (!resultTaskId.trim()) {
      return;
    }
    setActionPending(true);
    try {
      const response = await api.get(`/automations/celery/tasks/${encodeURIComponent(resultTaskId.trim())}/result`);
      setTaskResult(response.data);
    } catch (error) {
      setTaskResult({ success: false, error: error.response?.data?.error || error.message });
    } finally {
      setActionPending(false);
    }
  };

  const toggleQueueTask =

  (taskName, selected) => {
    setSelectedQueueTasks((previous) => ({ ...previous, [taskName]: selected }));
  };

  const renderOverview =

  () => {
    const overallSeverity = overview?.healthy ? 'success' : 'danger';
    const environment = overview?.environment || 'unknown';
    return (
      <header className="celery-card celery-dashboard__hero">
        <div>
          <div className="celery-dashboard__title-row">
            <h2 id="celery-operations-heading">Celery Operations</h2>
            <AppTag severity={overallSeverity} value={overview?.healthy ? 'Healthy' : 'Needs attention'} />
            <AppTag severity="warning" value={environment} />
          </div>
          <div className="celery-dashboard__meta">
            <span><b>Broker</b> {overview?.broker?.scheme || 'unknown'}</span>
            <span><b>Result backend</b> {overview?.result_backend?.scheme || 'unknown'}</span>
          </div>
        </div>
        <div className="celery-dashboard__hero-actions">
          {lastRefresh &&
            <span className="celery-dashboard__timestamp">Updated {lastRefresh.toLocaleTimeString()}</span>}
          <AppButton
            label="Refresh"
            icon="pi pi-refresh"
            variant="secondary"
            onClick={fetchDashboard}
            disabled={loading || actionPending} />
        </div>
      </header>
    );
  };

  const renderStats =

  () => {
    const stats = [
      { label: 'Workers', value: overview?.worker_count || 0, tone: 'workers' },
      { label: 'Active', value: overview?.active_count || 0, tone: 'active' },
      { label: 'Reserved', value: overview?.reserved_count || 0, tone: 'reserved' },
      { label: 'Scheduled', value: overview?.scheduled_count || 0, tone: 'scheduled' },
      { label: 'Queued', value: overview?.queued_count || 0, tone: 'queued' },
      { label: 'Beat entries', value: overview?.beat_schedule_count || 0, tone: 'beat' },
    ];
    return (
      <dl className="celery-dashboard__stats" aria-label="Celery status overview">
        {stats.map(({ label, value, tone }) =>
          <div key={label} className={`celery-stat celery-stat--${tone}`}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        )}
      </dl>
    );
  };

  const renderWorkers =

  () => (
    <section className="celery-card celery-card--workers" aria-labelledby="celery-workers-heading">
      <div className="celery-section-header">
        <div>
          <h3 id="celery-workers-heading">Workers</h3>
          <p>Live worker status, active queues, task registration, and worker-level controls.</p>
        </div>
      </div>
      {workers.length === 0 ?
        <div className="celery-empty">No Celery workers responded.</div> :
        <div className="celery-table">
          <div className="celery-table__header celery-table__row--workers">
            <div>Worker</div>
            <div>Queues</div>
            <div>Tasks</div>
            <div>Controls</div>
          </div>
          {workers.map((worker) =>
            <div key={worker.name} className="celery-table__row celery-table__row--workers">
              <div className="celery-table__cell celery-table__cell--primary" data-label="Worker">
                <div className="font-medium app-data-value">{worker.name}</div>
                <AppTag severity={worker.status === 'online' ? 'success' : 'warning'} value={worker.status} />
              </div>
              <div className="celery-table__cell celery-dashboard__wrap" data-label="Queues">
                {(worker.active_queues || []).map((queue) =>
                  <span key={queue.name || queue.routing_key} className="celery-chip">{queue.name || queue.routing_key}</span>
                )}
              </div>
              <div className="celery-table__cell celery-metric-line" data-label="Tasks">
                <span><strong>{worker.active_count || 0}</strong> active</span>
                <span><strong>{worker.reserved_count || 0}</strong> reserved</span>
                <span><strong>{worker.scheduled_count || 0}</strong> scheduled</span>
                <span><strong>{(worker.registered_tasks || []).length}</strong> registered</span>
              </div>
              <div className="celery-table__cell celery-table__cell--actions celery-dashboard__row-actions" data-label="Controls">
                <AppButton
                  label="Restart pool"
                  variant="secondary"
                  onClick={() => runAction({
                    endpoint: `/automations/celery/workers/${encodeURIComponent(worker.name)}/pool/restart`,
                    payload: {},
                    highRisk: true,
                    title: 'Restart worker pool?',
                    description: `Restart the execution pool for ${worker.name}.`,
                    successSummary: 'Pool restart sent',
                  })}
                  disabled={actionPending} />
                <AppButton
                  label="Shutdown"
                  variant="danger"
                  onClick={() => runAction({
                    endpoint: `/automations/celery/workers/${encodeURIComponent(worker.name)}/shutdown`,
                    payload: {},
                    highRisk: true,
                    title: 'Shutdown worker?',
                    description: `Shutdown ${worker.name}.`,
                    successSummary: 'Shutdown sent',
                  })}
                  disabled={actionPending} />
              </div>
            </div>
          )}
        </div>}
    </section>
  );

  const renderTasks =

  () => {
    const tasks = taskFilters.state === 'history' ? [] : displayTasks;
    const logs = displayLogs;
    return (
      <section className="celery-card celery-card--tasks" aria-labelledby="celery-tasks-heading">
        <div className="celery-section-header">
          <div>
            <h3 id="celery-tasks-heading">Tasks</h3>
            <p>Active, reserved, scheduled, revoked, and recent logged task activity.</p>
          </div>
          <div className="celery-dashboard__filters">
            <AppSelect
              value={taskFilters.state}
              options={TASK_STATE_OPTIONS}
              onChange={(value) => setTaskFilters((previous) => ({ ...previous, state: value }))} />
            <AppTextInput
              value={taskFilters.q}
              placeholder="Filter tasks"
              onChange={(event) => setTaskFilters((previous) => ({ ...previous, q: event.target.value }))} />
          </div>
        </div>

        {tasks.length === 0 ?
          <div className="celery-empty">No live tasks for this filter.</div> :
          <div className="celery-table">
            <div className="celery-table__header celery-table__row--tasks">
              <div>Task</div>
              <div>State</div>
              <div>Worker</div>
              <div>Controls</div>
            </div>
            {tasks.map((task) =>
              <div key={`${task.state}-${task.id}-${task.worker}`} className="celery-table__row celery-table__row--tasks">
                <div className="celery-table__cell celery-table__cell--primary" data-label="Task">
                  <div className="font-medium">{task.short_name || shortName(task.task_name)}</div>
                  <div className="text-xs text-600 app-data-value">{task.task_name || task.id}</div>
                  <div className="celery-code-preview app-data-value">{formatJsonPreview(task.args)} {formatJsonPreview(task.kwargs)}</div>
                </div>
                <div className="celery-table__cell" data-label="State"><AppTag severity={taskStateSeverity(task.state)} value={task.state} /></div>
                <div className="celery-table__cell app-data-value text-sm" data-label="Worker">{task.worker || '-'}</div>
                <div className="celery-table__cell celery-table__cell--actions celery-dashboard__row-actions" data-label="Controls">
                  {task.id &&
                    <AppButton
                      label="Revoke"
                      variant="secondary"
                      onClick={() => runAction({
                        endpoint: `/automations/celery/tasks/${encodeURIComponent(task.id)}/revoke`,
                        payload: { terminate: false, signal: 'SIGTERM' },
                        successSummary: 'Revoke sent',
                      })}
                      disabled={actionPending} />}
                  {task.id &&
                    <AppButton
                      label="Terminate"
                      variant="danger"
                      onClick={() => runAction({
                        endpoint: `/automations/celery/tasks/${encodeURIComponent(task.id)}/revoke`,
                        payload: { terminate: true, signal: 'SIGTERM' },
                        highRisk: true,
                        title: 'Terminate task?',
                        description: `Terminate task ${task.id}.`,
                        successSummary: 'Terminate sent',
                      })}
                      disabled={actionPending} />}
                </div>
              </div>
            )}
          </div>}

        <div className="celery-dashboard__subsection">
          <h4>Recent Task Logs</h4>
          {logs.length === 0 ?
            <div className="celery-empty">No recent Celery logs.</div> :
            <div className="celery-log-list">
              {logs.map((log) =>
                <div key={log.id} className="celery-log-row">
                  <AppTag severity={taskStateSeverity(log.status)} value={log.status} />
                  <span className="app-data-value">{log.task_name || log.action || log.name}</span>
                  <span>{log.message}</span>
                </div>
              )}
            </div>}
        </div>
      </section>
    );
  };

  const renderQueues =

  () => (
    <section className="celery-card celery-card--queues" aria-labelledby="celery-queues-heading">
      <div className="celery-section-header">
        <div>
          <h3 id="celery-queues-heading">Queues</h3>
          <p>Broker queue depth and grouped waiting messages by task type.</p>
        </div>
        <div className="celery-dashboard__filters">
          <AppSelect
            value={currentQueue?.queue || ''}
            options={queueOptions}
            placeholder="Select queue"
            onChange={(value) => {
              setSelectedQueueName(value);
              setSelectedQueueTasks({});
            }} />
          <AppButton label="Refresh" icon="pi pi-refresh" variant="secondary" onClick={fetchDashboard} disabled={loading || actionPending} />
        </div>
      </div>

      {!currentQueue ?
        <div className="celery-empty">No broker queues discovered.</div> :
        <>
          <dl className="celery-dashboard__queue-summary" aria-label={`${currentQueue.queue} queue status`}>
            <div className="celery-stat celery-stat--queue-name"><dt>Queue</dt><dd>{currentQueue.queue}</dd></div>
            <div className="celery-stat celery-stat--queued"><dt>Queued</dt><dd>{currentQueue.total || 0}</dd></div>
            <div className="celery-stat celery-stat--tasks"><dt>Task types</dt><dd>{queueGroups.length}</dd></div>
            <div className="celery-stat celery-stat--selected"><dt>Selected</dt><dd>{selectedQueueMessageCount}</dd></div>
          </dl>
          {currentQueue.success === false ?
            <div className="celery-empty celery-empty--error">{currentQueue.error || 'Unable to inspect queue'}</div> :
            queueGroups.length === 0 ?
            <div className="celery-empty">No queued tasks.</div> :
            <div className="celery-table">
              <div className="celery-table__header celery-table__row--queue">
                <div>Task</div>
                <div>Count</div>
                <div>Sample</div>
              </div>
              {queueGroups.map((group) => {
                const sample = group.samples?.[0] || {};
                const selected = Boolean(selectedQueueTasks[group.task_name]);
                return (
                  <div key={group.task_name} className="celery-table__row celery-table__row--queue">
                    <label className="celery-table__cell celery-dashboard__checkbox-row" data-label="Task">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(event) => toggleQueueTask(group.task_name, event.target.checked)}
                        aria-label={`Select ${group.short_name}`} />
                      <span>
                        <span className="font-medium">{group.short_name}</span>
                        <span className="text-xs text-600 app-data-value">{group.task_name}</span>
                        {group.job_name && <span className="text-xs text-600">{group.job_name}</span>}
                      </span>
                    </label>
                    <div className="celery-table__cell" data-label="Count"><AppTag severity={selected ? 'warning' : 'info'} value={String(group.count)} /></div>
                    <div className="celery-table__cell celery-code-preview app-data-value" data-label="Sample">{sample.id || 'no task id'} {sample.args || ''} {sample.kwargs || ''}</div>
                  </div>
                );
              })}
            </div>}
          <div className="celery-dashboard__actions-bar">
            <AppButton
              label="Remove selected"
              icon="pi pi-trash"
              variant="danger"
              onClick={() => runAction({
                endpoint: '/automations/celery/queue/purge',
                payload: { queue: currentQueue.queue, task_names: selectedQueueTaskNames },
                successSummary: 'Queued tasks removed',
              })}
              disabled={actionPending || selectedQueueTaskNames.length === 0} />
            <AppButton
              label="Remove all"
              icon="pi pi-times"
              variant="danger"
              onClick={() => runAction({
                endpoint: '/automations/celery/queue/purge',
                payload: { queue: currentQueue.queue, all: true },
                highRisk: true,
                title: 'Remove all queued tasks?',
                description: `Remove all ${currentQueue.total || 0} queued messages from ${currentQueue.queue}.`,
                successSummary: 'Queue purged',
              })}
              disabled={actionPending || Number(currentQueue.total || 0) === 0} />
          </div>
        </>}
    </section>
  );

  const renderControls =

  () => (
    <section className="celery-card celery-card--controls" aria-labelledby="celery-controls-heading">
      <div className="celery-section-header">
        <div>
          <h3 id="celery-controls-heading">Controls</h3>
          <p>Worker pool, queue consumer, task limit, result lookup, and known-task run controls.</p>
        </div>
      </div>

      <div className="celery-control-grid">
        <div className="celery-control-panel">
          <h4>Worker Pool</h4>
          <label>Worker</label>
          <AppSelect
            value={workerControl.worker}
            options={workerOptions}
            placeholder="Select worker"
            onChange={(value) => setWorkerControl((previous) => ({ ...previous, worker: value }))} />
          <label>Pool delta</label>
          <AppNumberInput
            value={workerControl.n}
            onChange={(value) => setWorkerControl((previous) => ({ ...previous, n: value || 1 }))}
            step={1} />
          <div className="celery-dashboard__row-actions">
            <AppButton
              label="Grow"
              variant="secondary"
              onClick={() => requireWorker() && runAction({
                endpoint: `/automations/celery/workers/${encodeURIComponent(workerControl.worker)}/pool/grow`,
                payload: { n: workerControl.n || 1 },
                successSummary: 'Pool grow sent',
              })}
              disabled={actionPending} />
            <AppButton
              label="Shrink"
              variant="secondary"
              onClick={() => requireWorker() && runAction({
                endpoint: `/automations/celery/workers/${encodeURIComponent(workerControl.worker)}/pool/shrink`,
                payload: { n: workerControl.n || 1 },
                successSummary: 'Pool shrink sent',
              })}
              disabled={actionPending} />
          </div>
          <div className="celery-dashboard__inline-fields">
            <div>
              <label>Min</label>
              <AppNumberInput value={workerControl.min} onChange={(value) => setWorkerControl((previous) => ({ ...previous, min: value ?? 0 }))} step={1} />
            </div>
            <div>
              <label>Max</label>
              <AppNumberInput value={workerControl.max} onChange={(value) => setWorkerControl((previous) => ({ ...previous, max: value || 1 }))} step={1} />
            </div>
          </div>
          <AppButton
            label="Autoscale"
            variant="danger"
            onClick={() => requireWorker() && runAction({
              endpoint: `/automations/celery/workers/${encodeURIComponent(workerControl.worker)}/pool/autoscale`,
              payload: { min: workerControl.min ?? 0, max: workerControl.max || 1 },
              highRisk: true,
              title: 'Autoscale worker pool?',
              description: `Change autoscale settings for ${workerControl.worker}.`,
              successSummary: 'Autoscale sent',
            })}
            disabled={actionPending} />
        </div>

        <div className="celery-control-panel">
          <h4>Queue Consumers</h4>
          <label>Queue</label>
          <AppTextInput
            value={workerControl.queue}
            onChange={(event) => setWorkerControl((previous) => ({ ...previous, queue: event.target.value }))}
            placeholder="celery" />
          <div className="celery-dashboard__row-actions">
            <AppButton
              label="Add consumer"
              variant="secondary"
              onClick={() => requireWorker() && runAction({
                endpoint: `/automations/celery/workers/${encodeURIComponent(workerControl.worker)}/queues/add-consumer`,
                payload: { queue: workerControl.queue || 'celery' },
                successSummary: 'Consumer added',
              })}
              disabled={actionPending} />
            <AppButton
              label="Cancel consumer"
              variant="danger"
              onClick={() => requireWorker() && runAction({
                endpoint: `/automations/celery/workers/${encodeURIComponent(workerControl.worker)}/queues/cancel-consumer`,
                payload: { queue: workerControl.queue || 'celery' },
                highRisk: true,
                title: 'Cancel queue consumer?',
                description: `${workerControl.worker} will stop consuming ${workerControl.queue || 'celery'}.`,
                successSummary: 'Consumer cancelled',
              })}
              disabled={actionPending} />
          </div>
        </div>

        <div className="celery-control-panel">
          <h4>Task Limits</h4>
          <label>Task</label>
          <AppSelect
            value={limitControl.task}
            options={taskOptions}
            placeholder="Select task"
            onChange={(value) => setLimitControl((previous) => ({ ...previous, task: value }))} />
          <label>Worker optional</label>
          <AppSelect
            value={limitControl.worker}
            options={workerOptions}
            placeholder="All workers"
            onChange={(value) => setLimitControl((previous) => ({ ...previous, worker: value }))} />
          <label>Rate limit</label>
          <AppTextInput
            value={limitControl.rate_limit}
            onChange={(event) => setLimitControl((previous) => ({ ...previous, rate_limit: event.target.value }))}
            placeholder="10/m" />
          <div className="celery-dashboard__row-actions">
            <AppButton
              label="Set rate"
              variant="secondary"
              onClick={() => limitControl.task && runAction({
                endpoint: `/automations/celery/tasks/${encodeURIComponent(limitControl.task)}/rate-limit`,
                payload: { rate_limit: limitControl.rate_limit, worker: limitControl.worker || undefined },
                successSummary: 'Rate limit sent',
              })}
              disabled={actionPending || !limitControl.task} />
          </div>
          <div className="celery-dashboard__inline-fields">
            <div>
              <label>Soft seconds</label>
              <AppNumberInput value={limitControl.soft} onChange={(value) => setLimitControl((previous) => ({ ...previous, soft: value || 1 }))} step={1} />
            </div>
            <div>
              <label>Hard seconds</label>
              <AppNumberInput value={limitControl.hard} onChange={(value) => setLimitControl((previous) => ({ ...previous, hard: value || 1 }))} step={1} />
            </div>
          </div>
          <AppButton
            label="Set time limit"
            variant="secondary"
            onClick={() => limitControl.task && runAction({
              endpoint: `/automations/celery/tasks/${encodeURIComponent(limitControl.task)}/time-limit`,
              payload: { soft: limitControl.soft || 1, hard: limitControl.hard || 1, worker: limitControl.worker || undefined },
              successSummary: 'Time limit sent',
            })}
            disabled={actionPending || !limitControl.task} />
        </div>

        <div className="celery-control-panel">
          <h4>Run Known Task</h4>
          <label>Task</label>
          <AppSelect
            value={runControl.task}
            options={taskOptions}
            placeholder="Select task"
            onChange={(value) => setRunControl((previous) => ({ ...previous, task: value }))} />
          <label>Args JSON</label>
          <AppTextarea rows={2} value={runControl.args} onChange={(value) => setRunControl((previous) => ({ ...previous, args: value }))} />
          <label>Kwargs JSON</label>
          <AppTextarea rows={2} value={runControl.kwargs} onChange={(value) => setRunControl((previous) => ({ ...previous, kwargs: value }))} />
          <label>Options JSON</label>
          <AppTextarea rows={2} value={runControl.options} onChange={(value) => setRunControl((previous) => ({ ...previous, options: value }))} />
          <AppButton label="Run task" icon="pi pi-play" onClick={handleTaskRun} disabled={actionPending || !runControl.task} />
        </div>

        <div className="celery-control-panel">
          <h4>Result Lookup</h4>
          <label>Task id</label>
          <AppTextInput value={resultTaskId} onChange={(event) => setResultTaskId(event.target.value)} placeholder="Celery task id" />
          <AppButton label="Lookup" variant="secondary" onClick={handleResultLookup} disabled={actionPending || !resultTaskId.trim()} />
          {taskResult &&
            <div className="celery-result-box">
              {taskResult.success === false ?
                <span>{taskResult.error}</span> :
                <>
                  <AppTag severity={taskStateSeverity(String(taskResult.state || '').toLowerCase())} value={taskResult.state} />
                  <div className="app-data-value">{taskResult.task_id}</div>
                  <pre>{taskResult.result || taskResult.traceback || 'No result available yet'}</pre>
                </>}
            </div>}
        </div>
      </div>
    </section>
  );

  return (
    <section className="celery-dashboard" aria-labelledby="celery-operations-heading">
      {renderOverview()}
      {Object.keys(panelErrors).length > 0 &&
        <div className="celery-empty celery-empty--error">
          {Object.entries(panelErrors).map(([key, value]) => <div key={key}>{key}: {value}</div>)}
        </div>}
      {loading && !overview ?
        <div className="celery-dashboard__loading">
          <AppSpinner />
          <span>Loading Celery dashboard...</span>
        </div> :
        <>
          {renderStats()}
          {renderWorkers()}
          {renderTasks()}
          {renderQueues()}
          {renderControls()}
        </>}

      <AppModal
        visible={!!confirmAction}
        className="celery-confirmation-modal"
        title={confirmAction?.title || 'Confirm Celery action'}
        description={confirmAction?.description}
        width="520px"
        onClose={() => {
          if (!actionPending) {
            setConfirmAction(null);
            setConfirmationText('');
          }
        }}
        footer={
          <div className="flex justify-content-end gap-2">
            <AppButton
              label="Cancel"
              variant="secondary"
              onClick={() => {
                setConfirmAction(null);
                setConfirmationText('');
              }}
              disabled={actionPending} />
            <AppButton
              label="Confirm"
              icon="pi pi-check"
              variant="danger"
              onClick={() => confirmAction && executeAction(confirmAction)}
              disabled={actionPending || confirmationText !== CONFIRMATION_TOKEN} />
          </div>
        }>
        <div className="celery-confirmation-copy">
          Type <span className="app-data-value">{CONFIRMATION_TOKEN}</span> to continue.
        </div>
        <AppTextInput
          value={confirmationText}
          onChange={(event) => setConfirmationText(event.target.value)}
          placeholder={CONFIRMATION_TOKEN}
          autoFocus />
      </AppModal>
    </section>
  );
}

export default CeleryDashboard;
